/**
 * CBUAE's vocabulary (P415).
 *
 * The bundled CBUAE content is one catalogue (Outsourcing, P332), so
 * this regime has a narrower footprint than SAMA or OSFI -- but the
 * shape is closed and every key is answered, because a half-filled
 * overlay renders another regulator's word in the gaps and looks
 * authoritative doing it.
 *
 * Where CBUAE has not published a distinct term for a concept MARSAD
 * models, the neutral English is used rather than SAMA's. "Important
 * business service" is SAMA's phrase; a CBUAE screen borrowing it
 * would be asserting something about Emirati supervision that nobody
 * has checked.
 */
import { markUntranslated } from "shared/i18n/markUntranslated";

import type { RegimeVocab, RegimeVocabByLanguage } from "shared/i18n/regime/types";

const en: RegimeVocab = {
  serviceTerm: "critical business service",
  serviceTermPlural: "critical business services",
  serviceTermShort: "critical service",
  serviceTermShortPlural: "critical services",
  serviceTermShortA: "a critical service",
  serviceTermTitle: "Critical business service",
  serviceTermPluralTitle: "Critical business services",
  serviceTermDefinition:
    "A service whose disruption would cause unacceptable harm to " +
    "customers or to the stability of the financial system.",

  toleranceTerm: "impact tolerance",
  toleranceTermPlural: "impact tolerances",
  toleranceTermTitle: "Impact Tolerance",
  riskLimitTerm: "headroom",

  institutionTerm: "institution",
  institutionTermPlural: "institutions",
  institutionClassTerm: "licensed financial institution",
  regulatorName: "Central Bank of the United Arab Emirates",
  regulatorShort: "CBUAE",
  examinerTerm: "examiner",
  letterTerm: "regulator letter",

  mvbTerm: "minimum viable operations",
  mvbTermTitle: "Minimum viable operations",
  mvbTermShort: "minimum set",

  admissionPathway: {
    ces: "not used under this regime",
    ids: "not used under this regime",
    critical_operation: "Critical service identification",
  },

  outcomeCategory: {
    minimize_customer_harm: "Minimise customer harm",
    retain_customer_confidence: "Retain customer confidence",
    regulatory_engagement: "Regulatory engagement",
    protect_safety_and_soundness: "Protect safety and soundness",
  },
  outcomeCategoryDefinition: {
    minimize_customer_harm:
      "Disruption would cause customers financial loss or deny them " +
      "access to their money.",
    retain_customer_confidence:
      "Disruption would damage trust in the institution or the payment " +
      "system it participates in.",
    regulatory_engagement:
      "Disruption would breach a supervised obligation or stop the " +
      "institution reporting to the Central Bank.",
    protect_safety_and_soundness:
      "Disruption would threaten the institution's viability or the " +
      "stability of the wider system.",
  },

  boardPackTitle: "Board resilience snapshot",
  currencySymbol: "AED",
};

export const cbuae: RegimeVocabByLanguage = {
  en,
  ar: markUntranslated(en) as RegimeVocab,
};
